import React from "react";
import { generatedVideoMaxSeconds, launchPageSeconds, launchTemplates } from "@veyso/core";

/**
 * How long the film should run.
 *
 * Null means "whatever the look wants", which is the right default and not a
 * dodge: a teaser style asks for sixteen seconds because that is the form, and
 * overriding it to fifty produces fifty seconds of restraint rather than a
 * grander film.
 *
 * A short list rather than a number field. The structure has six beats in
 * fixed proportions, so the meaningful choices are coarse — a teaser, the
 * usual thing, or a longer one — and asking for 37 seconds implies a precision
 * nothing downstream honours.
 */
const CHOICES: { seconds: number; label: string; hint: string }[] = [
  { seconds: 16, label: "16s", hint: "A teaser" },
  { seconds: 30, label: "30s", hint: "Short" },
  { seconds: launchPageSeconds, label: `${launchPageSeconds}s`, hint: "The usual length" },
  { seconds: 70, label: "70s", hint: "Room for more detail" },
];

export const LengthPicker: React.FC<{
  value: number | null;
  onChange: (seconds: number | null) => void;
  templateId: string | null;
  disabled?: boolean;
}> = ({ value, onChange, templateId, disabled }) => {
  const chosen = launchTemplates.find((one) => one.id === templateId);
  const wants = chosen?.seconds;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(null)}
          aria-pressed={value === null}
          className={`rounded-[7px] border px-3 py-1.5 text-[12.5px] font-medium transition-colors ${
            value === null
              ? "border-ember-500 bg-ember-500/5 text-ink-100"
              : "border-[var(--edge)] text-ink-300 hover:border-[var(--edge-strong)]"
          }`}
        >
          {wants ? `As the style wants (${wants}s)` : `Default (${launchPageSeconds}s)`}
        </button>

        {CHOICES.filter((choice) => choice.seconds <= generatedVideoMaxSeconds).map((choice) => (
          <button
            key={choice.seconds}
            type="button"
            disabled={disabled}
            onClick={() => onChange(choice.seconds)}
            aria-pressed={value === choice.seconds}
            title={choice.hint}
            className={`tnum rounded-[7px] border px-3 py-1.5 text-[12.5px] font-medium transition-colors ${
              value === choice.seconds
                ? "border-ember-500 bg-ember-500/5 text-ink-100"
                : "border-[var(--edge)] text-ink-300 hover:border-[var(--edge-strong)]"
            }`}
          >
            {choice.label}
          </button>
        ))}
      </div>

      <p className="text-[11.5px] leading-[1.5] text-ink-400">
        {value === null && wants
          ? `${chosen?.name} is written as a ${wants}-second teaser. Overriding that makes it longer, not bigger.`
          : "The six beats keep their proportions, so a longer film gives each section more room rather than adding sections."}
      </p>
    </div>
  );
};
